import { createPortal } from "react-dom";
import { IconoAlerta, IconoCheck } from "../../AuditoriaClinica/rediseno/AuditIconos";
import useDialogo from "./useDialogo";
import "./Modal.css";

/* ============================================================================
   REDISENO-SMARTMEDIC · FASE 4 — Modal

   Las reglas del documento están METIDAS EN EL COMPONENTE, no confiadas a
   quien lo use:

   · Cuatro anchos, no libres (`aviso` 400 · `confirmacion` 480 ·
     `formulario` 560 · `contenido` 720). No hay pantalla completa.
   · El pie siempre igual: secundario y luego primario, a la derecha. El texto
     del primario lo pone quien llama y dice la acción concreta.
   · Destructivo: icono en círculo rojo y el PRIMARIO en rojo, nunca al revés.
   · Esc y clic fuera cierran, salvo si hay cambios sin guardar: entonces se
     pide confirmación.
   · Trampa de foco mientras está abierto y el foco vuelve al elemento que lo
     abrió al cerrarse.

   Lo que este componente NO hace, a propósito: editar registros largos. Para
   eso está la edición en línea en la página (ver Auditoría Clínica). El modal
   es para confirmar, avisar o pedir de 1 a 4 campos.
   ========================================================================== */

export const ANCHOS = ["aviso", "confirmacion", "formulario", "contenido"];

/* La pila de diálogos, el Esc/backdrop con confirmación y la trampa de foco
   viven en `useDialogo` -- los comparte con HCModal (Historia Clínica), que
   necesita exactamente el mismo comportamiento sobre otra anatomía. */

function Modal({
    abierto,
    titulo,
    ancho = "confirmacion",
    destructivo = false,
    hayCambiosSinGuardar = false,
    mensajeDescartar = "Hay cambios sin guardar. ¿Descartarlos?",
    nota,
    secundario,
    primario,
    onCerrar,
    children,
}) {
    const anchoSeguro = ANCHOS.includes(ancho) ? ancho : "confirmacion";
    const { cajaRef, intentarCerrar } = useDialogo({
        abierto,
        hayCambiosSinGuardar,
        mensajeDescartar,
        onCerrar,
    });

    if (!abierto) return null;

    const contenido = (
        <div
            className="md__overlay"
            data-testid="modal-overlay"
            onMouseDown={(e) => {
                if (e.target === e.currentTarget) intentarCerrar();
            }}
        >
            <div
                className={`md md--${anchoSeguro}`}
                role="dialog"
                aria-modal="true"
                aria-label={titulo}
                data-ancho={anchoSeguro}
                data-testid="modal"
                ref={cajaRef}
                tabIndex={-1}
            >
                <div className="md__cabecera">
                    <h2 className="md__titulo">{titulo}</h2>
                    <button
                        type="button"
                        className="md__cerrar"
                        aria-label="Cerrar"
                        onClick={intentarCerrar}
                        data-testid="modal-cerrar"
                    >
                        ✕
                    </button>
                </div>

                <div className="md__cuerpo">
                    {destructivo ? (
                        <div className="md__destructivo">
                            <span className="md__destructivo-icono" aria-hidden="true">
                                <IconoAlerta size={20} />
                            </span>
                            <div>{children}</div>
                        </div>
                    ) : (
                        children
                    )}
                </div>

                <div className="md__pie">
                    {nota && <span className="md__pie-nota">{nota}</span>}
                    <div className="md__pie-acciones">
                        {secundario && (
                            <button
                                type="button"
                                className="md__btn"
                                onClick={secundario.onClick || intentarCerrar}
                                disabled={secundario.disabled}
                                data-testid="modal-secundario"
                            >
                                {secundario.label}
                            </button>
                        )}
                        {primario && (
                            <button
                                type="button"
                                className={`md__btn ${destructivo ? "md__btn--peligro" : "md__btn--primario"}`}
                                onClick={primario.onClick}
                                disabled={primario.disabled || primario.cargando}
                                data-testid="modal-primario"
                            >
                                {!destructivo && primario.icono !== false && <IconoCheck size={15} />}
                                {primario.cargando ? "Guardando…" : primario.label}
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );

    return createPortal(contenido, document.body);
}

/**
 * Resumen de consecuencias para una confirmación que cierra un flujo: qué se
 * está cerrando exactamente, no un "¿Está seguro?" a secas.
 * `filas`: [{ etiqueta, valor, tono: "ok" | "aviso" | "neutro" }]
 */
export function ResumenConsecuencias({ filas }) {
    if (!filas || filas.length === 0) return null;
    return (
        <div className="md__resumen" data-testid="modal-resumen">
            {filas.map((fila) => (
                <div className="md__resumen-fila" key={fila.etiqueta}>
                    <span className={`md__resumen-icono md__resumen-icono--${fila.tono || "neutro"}`}>
                        {fila.tono === "aviso" ? <IconoAlerta size={16} /> : <IconoCheck size={16} />}
                    </span>
                    <span>{fila.etiqueta}</span>
                    <span className="md__resumen-valor">{fila.valor}</span>
                </div>
            ))}
        </div>
    );
}

export default Modal;
